import React, { useState } from "react";
import { Navigator, useNavigate } from "react-router-dom";
import FeatureCard from "components/FeatureCard";
import inkvoydLogo from "../assets/inkvoydLogo.png";
import horizontalLogo from "../assets/LandingPageHorizontalLogo.png"

// --- Lightweight, drop-in components (replace with your own later) ---
const Modal: React.FC<{ title: string; buttonText: string } & React.PropsWithChildren> = ({ title, buttonText, children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      <button onClick={() => navigate(`/login`)} className="px-5 py-3 rounded-2xl shadow font-semibold bg-white text-gray-900 hover:shadow-md">
        {buttonText}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-xl font-bold text-gray-900">{title}</h3>
              <button onClick={() => setOpen(false)} className="px-3 py-1 text-sm rounded-lg bg-gray-100 hover:bg-gray-200">Close</button>
            </div>
            <div className="mt-4">
              {children ?? (
                <form className="space-y-3">
                  <input type="text" placeholder="Your name" className="w-full rounded-xl border border-gray-300 p-3" />
                  <input type="email" placeholder="Email address" className="w-full rounded-xl border border-gray-300 p-3" />
                  {/* Add any extra fields you need */}
                  <button type="button" className="w-full rounded-xl bg-gray-900 py-3 font-semibold text-white hover:opacity-90" onClick={() => navigate(`/login`)}>
                    Join now
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Testimonial: React.FC<{ quote: string; author: string; role?: string }>
  = ({ quote, author, role }) => (
    <div className="rounded-2xl bg-white/90 p-5 shadow">
      <p className="text-gray-800">{quote}</p>
      <div className="mt-3 text-sm text-gray-600">{author}{role ? `, ${role}` : ""}</div>
    </div>
  );

// --- Page ---
const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#eae6df] text-[#2f2f2d]">
      {/* Nav */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 text-[#2f2f2d]">
        <div className="flex items-center gap-3">
          <img src={inkvoydLogo} alt="InkVoyd Logo" className="h-8 w-8 rounded-xl bg-white/80" />
          <span className="text-xl font-extrabold tracking-tight">InkVoyd</span>
        </div>
        <nav className="hidden gap-6 text-sm md:flex">
          <a href="#features" className="font-bold hover:underline">Features</a>
          <a href="#how" className="font-bold hover:underline">How it works</a>
          <a href="#pricing" className="font-bold hover:underline">Pricing</a>
          <a href="#faq" className="font-bold hover:underline">FAQ</a>
        </nav>
        <div className="hidden md:block">
          <Modal title="Sign up for InkVoyd now!" buttonText="Sign up" />
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-5 py-16 md:grid-cols-2">
        <div className="rounded-3xl bg-white/90 p-8 shadow-2xl">
          <h1 className="text-4xl font-extrabold text-[#2f2f2d] md:text-6xl">Fill your books like you pack a sleeve.</h1>
          <p className="mt-4 text-lg text-[#2f2f2d]">We cover the negative space to make your work look great.
            We take care of the BS so you can focus on tattooing, not admin work.</p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Modal title="Sign up for the InkVoyd Waiting List" buttonText="Join the waiting list" />
            <a href="#features" className="inline-flex items-center justify-center rounded-2xl border border-white/70 px-5 py-3 font-semibold text-[#2f2f2d] backdrop-blur hover:bg-white/10">
              Learn more
            </a>
          </div>
          <p className="mt-3 text-xs text-[#2f2f2d]">Limited Availability. We're looking for tattoo artists</p>
        </div>
        <div className="flex min-h-[260px] items-center justify-center">
          {<img src={horizontalLogo} alt="InkVoyd Logo" className="h-64 w-full rounded-3xl bg-white/30 shadow-inner"/>}
        </div>
      </section>

      {/* Logos / Social proof (placeholder) 
      <section className="mx-auto max-w-6xl px-5 py-8">
        <div className="rounded-2xl bg-white/70 p-6 text-center text-sm text-[#2f2f2d]">
          Trusted by {/* Company logos go here  <span className="font-semibold">[logos here]</span>
        </div>
      </section>
      */}

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-3xl font-bold text-[#2f2f2d]">We're a small team so we can move fast. Check out our current features!</h2>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard title="Customer Obsession" description="We listen and we implement. Have new ideas? Something feel clunky? We live off of your feedback! Don't ever hesitate to reach out." />
          <FeatureCard title="Form Builder" description="Your very own form builder. No more signing in to different websites to configure your process. Everything is in one place!" />
          <FeatureCard title="Automated Workflows" description="Create automated workflows with your own customizable settings. Assign teammates to review client form submissions and tattoo ideas. If they think it's a good project, they'll pass it back to you." />
          <FeatureCard title="Centralized Platform" description="We provide a centralized hub for your booking and tattooing process. No more viewing form submissions in your email or upcoming appointments in your calendar. Everything is in one place!" />
          <FeatureCard title="Customer Response Time" description="Eliminate 1/2 the time needed to respond to customers. No more worrying about time bandits. If you have any questions, you start a chat - Not the other way around" />
          <FeatureCard title="Traveling Tattoo Artist" description="Got plans to tour the country? Network with other artists on our platform.  Don't worry about coordinating with your customers either, let our software take care of it." />
          <FeatureCard title="Security Deposit and Appointment Policies" description="Bunch of no shows? Don't worry, we'll take care of it. Just implement and adjust your own security deposit and appointment policies to your needs." />
          <FeatureCard title="Last Minute Cancellation" description="Last minute cancellation? No problem, with our automated features, we send out a notification to your interested clients." />
          <FeatureCard title="Team Management" description="Multiple people on your team? Assign each individual a responsibility in your booking process. Form submission review, form creation, calendar appointments, etc." />
          <FeatureCard title="Transparency" description="We believe in transparency, accept or reject your tattoo requests. Don't worry, rejections are common in this line of work and we make it easy on the customer." />
          {/* <FeatureCard title="Referral Program" description="Receive a tattoo request that isn't your style? Refer another artist you know and earn a commission" /> */}
          <FeatureCard title="Community Building" description="Build a network with your customers and tattoo artists. Let the community know about your upcoming flash sales, deals, tours, and more!" />
          <FeatureCard title="Social Media Integration (Coming soon)" description="Integrated social media. We can post your openings and schedule via your social media!" />
          <FeatureCard title="Multiple Payment Plans (Coming Soon)" description="We've integrated with Stripe so payments can be sent straight to your bank account. We only take a flat fee so you can add the charge to your customer or already include it in your orders. Don't want commissions? Stick to our monthly subscription plan instead!" />
          {/* <FeatureCard title="Subscription Plans" description="Build a subscription plan for users: Subscribed users will get first entry when books are opened and can skip queues to the very front." /> */}
          {/* <FeatureCard title="Analytics" description="We provide analytics so you know which part of your booking process is lacking." /> */}
          <FeatureCard title="Appointment Reminders" description="Send appointment reminders to your clients automatically." />
          <FeatureCard title="Feedback" description="We love your feedback! The founder will reach out to you and work with you one-on-one to ensure your needs are met." />
        </div>
      </section>

      {/* How it works (3-step) */}
      <section id="how" className="mx-auto max-w-6xl px-5 py-16">
        <div className="rounded-3xl bg-white/90 p-8 shadow-2xl">
          <h2 className="text-2xl font-bold text-[#2f2f2d]">How it works</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-gray-200 p-5">
              <div className="mb-2 text-sm font-semibold text-gray-500">01</div>
              <h3 className="text-lg font-semibold text-[#2f2f2d]">Create your workflow</h3>
              <p className="mt-1 text-sm text-[#2f2f2d]">Design your form(s), assign responsibilities to your team, choose your working schedule. Create security deposit polices, appointment reminders and more.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-5">
              <div className="mb-2 text-sm font-semibold text-gray-500">02</div>
              <h3 className="text-lg font-semibold text-[#2f2f2d]">Share your page link</h3>
              <p className="mt-1 text-sm text-[#2f2f2d]">Let the user visit your page and fill out the form. Your automated workflow will take care of the rest.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-5">
              <div className="mb-2 text-sm font-semibold text-gray-500">03</div>
              <h3 className="text-lg font-semibold text-[#2f2f2d]">Book & manage</h3>
              <p className="mt-1 text-sm text-[#2f2f2d]">Keep the notes and we'll send you reminders for your bookings. </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Social Proof */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-3xl font-bold text-[#2f2f2d]">Limited Availability</h2>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-1">
          <Testimonial quote="This is a service that values your feedback. We're looking to work with 5 artists to help streamline your processes. Help us help you" author="" role="" />
        </div>
      </section>

      {/* Pricing (placeholder) */}
      {/* <section id="pricing" className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-3xl font-bold text-[#2f2f2d]">Pricing</h2>
        <p className="mt-1 text-[#2f2f2d]/80">There are no pricing tiers for now.  This service is on-the-house to the first five artists that are willing to work with us.</p>
        <div className="mt-8 grid grid-cols-3 gap-6 md:grid-cols-3">
          {["Starter", "Pro", "Studio"].map((tier) => (
            <div key={tier} className="rounded-2xl bg-white/90 p-6 shadow">
              <h3 className="text-xl font-bold text-[#2f2f2d]">{tier}</h3>
              <p className="mt-1 text-sm text-[#2f2f2d]">Pricing TBD</p>
              <ul className="mt-4 space-y-2 text-sm text-[#2f2f2d]">
                <li>• Feature one</li>
                <li>• Feature two</li>
                <li>• Feature three</li>
              </ul>
              <div className="mt-6">
                <Modal title={`Get ${tier}`} buttonText="Choose plan" />
              </div>
            </div>
          ))}
        </div>
      </section> */}

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-6xl px-5 py-16">
        <div className="rounded-3xl bg-white/90 p-8 shadow-2xl">
          <h2 className="text-2xl font-bold text-[#2f2f2d]">FAQ</h2>
          <div className="mt-6 space-y-4">
            <details className="group rounded-xl border border-gray-200 p-4">
              <summary className="cursor-pointer list-none font-semibold text-[#2f2f2d]">Who's this software for?</summary>
              <p className="mt-2 text-sm text-[#2f2f2d]">InkVoyd is for all independent artists. From artists just starting out to seasoned professionals with their own internal teams that have to navigate the complexities of their craft. This software brings together all of the tools and resources needed to succeed in one convenient platform.</p>
            </details>
            <details className="group rounded-xl border border-gray-200 p-4">
              <summary className="cursor-pointer list-none font-semibold text-[#2f2f2d]">Is InkVoyd for studios?</summary>
              <p className="mt-2 text-sm text-[#2f2f2d]">No, InkVoyd is designed specifically for independent artists and their unique needs.</p>
            </details>
            <details className="group rounded-xl border border-gray-200 p-4">
              <summary className="cursor-pointer list-none font-semibold text-[#2f2f2d]">I have my own website, can I still use InkVoyd?</summary>
              <p className="mt-2 text-sm text-[#2f2f2d]">This is still in the works, but our software is an API which means your website will be able to integrate our features.</p>
            </details>
          </div>
          <div className="mt-6">
            <Modal title="Still have questions?" buttonText="Contact us" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="rounded-3xl bg-white/90 p-8 text-center shadow-2xl">
          <h2 className="text-2xl font-bold text-[#2f2f2d]">Ready to streamline bookings?</h2>
          <p className="mt-1 text-[#2f2f2d]">Reiterate your main call-to-action.</p>
          <div className="mt-4 flex justify-center">
            <Modal title="Sign up for the InkVoyd Waiting List" buttonText="Get started" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/20 bg-black/20 py-8 text-[#2f2f2d]">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-5 md:grid-cols-4">
          <div>
            <div className="text-lg font-bold">InkVoyd</div>
            <p className="mt-2 text-sm text-[#2f2f2d]/80">Filling your books like you pack a sleeve. A service made especially for tattoo artists</p>
          </div>
          <div>
            <div className="text-sm font-semibold">Product</div>
            <ul className="mt-2 space-y-2 text-sm text-[#2f2f2d]/80">
              <li><a href="#features" className="hover:underline">Features</a></li>
              <li><a href="#pricing" className="hover:underline">Pricing</a></li>
              <li><a href="#" className="hover:underline">Changelog</a></li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold">Company</div>
            <ul className="mt-2 space-y-2 text-sm text-[#2f2f2d]/80">
              <li><a href="#" className="hover:underline">About</a></li>
              <li><a href="#" className="hover:underline">Blog</a></li>
              <li><a href="#" className="hover:underline">Careers</a></li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold">Legal</div>
            <ul className="mt-2 space-y-2 text-sm text-[#2f2f2d]/80">
              <li><a href="#" className="hover:underline">Privacy</a></li>
              <li><a href="#" className="hover:underline">Terms</a></li>
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-8 max-w-6xl px-5 text-xs text-[#2f2f2d]/60">© {new Date().getFullYear()} InkVoyd. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default LandingPage;